"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const prompts_1 = require("@inquirer/prompts");
// ==================== 国际化（i18n）核心状态与字典 ====================
let currentLocale = 'zh-CN';
const defaultDictionary = {
    'zh-CN': {
        'prompt.modelPath': '请输入 Ollama 模型文件存储路径（留空则使用默认系统路径）:',
        'prompt.origins': '请输入允许跨域请求的来源 (OLLAMA_ORIGINS):',
        'prompt.model': '请选择您想要初始化的首选大模型:',
        'choice.skip': '跳过模型下载',
        'search.noResults': '未找到匹配的命令或模型',
    },
    en: {
        'prompt.modelPath': 'Enter Ollama model storage path (leave blank for default):',
        'prompt.origins': 'Enter allowed CORS origins (OLLAMA_ORIGINS):',
        'prompt.model': 'Select your preferred initial large language model:',
        'choice.skip': 'Skip model download',
        'search.noResults': 'No matching commands or models found',
    },
};
const userDictionaries = {};
const Prompt = {
    /**
     * 设置当前全局语言
     * @param locale 语言代号，如 'zh-CN' 或 'en'
     */
    setLocale(locale) {
        currentLocale = locale;
    },
    /**
     * 获取当前语言代号
     */
    getLocale() {
        return currentLocale;
    },
    /**
     * 注册或扩充自定义翻译字典
     * @param locale 语言代号
     * @param translations 翻译键值对
     */
    addTranslations(locale, translations) {
        userDictionaries[locale] = {
            ...(userDictionaries[locale] || {}),
            ...translations,
        };
    },
    /**
     * 翻译函数：支持传入 i18n Key 或普通文本。如果 Key 存在于字典中则返回翻译，否则返回原文本。
     */
    t(keyOrText, locale = currentLocale) {
        if (!keyOrText)
            return '';
        // 1. 优先从用户自定义字典查找
        if (userDictionaries[locale]?.[keyOrText]) {
            return userDictionaries[locale][keyOrText];
        }
        // 2. 从内置默认字典查找
        if (defaultDictionary[locale]?.[keyOrText]) {
            return defaultDictionary[locale][keyOrText];
        }
        // 3. 没找到则直接把原字符串返回（支持直接写中文/英文提示词）
        return keyOrText;
    },
    /**
     * 为 CLI 提示添加交互问题（底层适配 @inquirer/prompts 组件化生态，并自动应用 i18n）
     */
    async addQuestions(questions) {
        const questionList = Array.isArray(questions) ? questions : [questions];
        const answers = {};
        for (const q of questionList) {
            // 1. 处理 when 条件判断
            if (q.when !== undefined) {
                const shouldAsk = typeof q.when === 'function' ? await q.when(answers) : q.when;
                if (!shouldAsk) {
                    continue;
                }
            }
            const name = q.name || 'unnamed';
            const type = q.type || 'input';
            // 2. 处理动态 message 并通过 i18n.t() 翻译
            let rawMessage = '';
            if (typeof q.message === 'function') {
                rawMessage = await q.message(answers);
            }
            else {
                rawMessage = q.message || '';
            }
            const message = Prompt.t(rawMessage);
            // 3. 处理动态 default
            const defaultValue = typeof q.default === 'function' ? await q.default(answers) : q.default;
            // 4. 处理动态 choices 并对 item.name 进行国际化翻译
            let choicesVal;
            if (q.choices) {
                const rawChoices = typeof q.choices === 'function' ? await q.choices(answers) : q.choices;
                choicesVal = rawChoices.map((item) => {
                    if (typeof item === 'object' && item !== null) {
                        return {
                            ...item,
                            name: item.name ? Prompt.t(item.name) : undefined,
                        };
                    }
                    return { name: Prompt.t(String(item)), value: item };
                });
            }
            // 5. 组装 @inquirer/prompts 所需的配置参数
            const promptConfig = {
                message,
            };
            if (defaultValue !== undefined) {
                promptConfig.default = defaultValue;
            }
            if (choicesVal !== undefined) {
                promptConfig.choices = choicesVal;
            }
            if (q.pageSize !== undefined) {
                promptConfig.pageSize = q.pageSize;
            }
            if (q.transformer !== undefined) {
                promptConfig.transformer = q.transformer;
            }
            if (q.validate !== undefined) {
                promptConfig.validate = (inputVal) => q.validate(inputVal, answers);
            }
            // 6. 支持 search/autocomplete 的 source 预测函数，并翻译结果
            if (q.source) {
                promptConfig.source = async (term) => {
                    const rawResult = await q.source(term, answers);
                    return rawResult.map((item) => {
                        if (typeof item === 'object' && item !== null) {
                            return {
                                ...item,
                                name: item.name ? Prompt.t(item.name) : undefined,
                            };
                        }
                        return { name: Prompt.t(String(item)), value: item };
                    });
                };
            }
            // 7. 根据 type 映射调用对应的 @inquirer/prompts 组件
            let answer;
            switch (type) {
                case 'input':
                    answer = await (0, prompts_1.input)(promptConfig);
                    break;
                case 'number':
                    answer = await (0, prompts_1.number)(promptConfig);
                    break;
                case 'confirm':
                    answer = await (0, prompts_1.confirm)(promptConfig);
                    break;
                case 'select':
                case 'list':
                case 'rawlist':
                case 'expand':
                    answer = await (0, prompts_1.select)(promptConfig);
                    break;
                case 'search':
                case 'autocomplete':
                    answer = await (0, prompts_1.search)(promptConfig);
                    break;
                case 'checkbox':
                    answer = await (0, prompts_1.checkbox)(promptConfig);
                    break;
                case 'password':
                    answer = await (0, prompts_1.password)(promptConfig);
                    break;
                case 'editor':
                    answer = await (0, prompts_1.editor)(promptConfig);
                    break;
                default:
                    answer = await (0, prompts_1.input)(promptConfig);
                    break;
            }
            // 8. 处理 filter 转换
            if (q.filter) {
                answer = await q.filter(answer, answers);
            }
            answers[name] = answer;
        }
        return answers;
    },
};
exports.default = Prompt;
